import { useQuery } from "react-query"
import styled, { css } from "styled-components"
import {
	Flex,
	Text,
	breakpoint,
	INNER_MAX_WIDTH,
	scale,
	colors,
} from "@capioweb/ui-components/src"
import { PUBLIC_API_ENDPOINT, queryStaleTime } from "../../constants"
import { assetBaseUrl, urlIsInternal } from "../../utils"
import { getContent } from "../../api/content"
import { MenuItem } from "../../@types/CapioWeb/menu"
import { FooterQueryProps } from "../Core"
import { Link } from "react-router-dom"
import { sendNavigationDataLayerPush } from "../../utils/piwikPro"
import React from "react"
import useSSR from "use-ssr"
import debounce from "lodash.debounce"
import { BackToTop } from "@capioweb/ui-components/src"

interface FooterLinkSetProps {
	link?: MenuItem
	children?: React.ReactNode
}

const FooterLinkSetWrapper = styled.div`
	break-inside: avoid;
	font-size: ${scale.px(1.75)};
	line-height: ${scale.px(2)};

	${breakpoint("l")`
		font-size: ${scale.px(2)};
		line-height: ${scale.px(2.5)};
    `}

	&:after {
		content: "\\00a0";
		display: block;
		margin-bottom: ${scale.px(3)};
	}
`

const footerLinkCommonStyles = css`
	display: inline;
	color: ${colors.blue33};

	&:hover {
		color: ${colors.white};
		text-decoration: underline;
	}
`

const FooterLinkExternal = styled.a`
	${footerLinkCommonStyles}
`

const FooterLinkInternal = styled(Link)`
	${footerLinkCommonStyles}
`

const FooterLink: React.FC<FooterLinkSetProps> = ({ link }) => {
	if (!urlIsInternal(link.url)) {
		return (
			<FooterLinkExternal
				href={link.url}
				onClick={() => sendNavigationDataLayerPush("footer", link.name)}
				target={link.target}
			>
				{link.name}
			</FooterLinkExternal>
		)
	} else {
		return (
			<FooterLinkInternal
				to={link?.url ?? "#"}
				onClick={() => sendNavigationDataLayerPush("footer", link.name)}
			>
				{link.name}
			</FooterLinkInternal>
		)
	}
}

const FooterLinkSet: React.FC<FooterLinkSetProps> = ({ link }) => (
	<FooterLinkSetWrapper>
		<Text
			variant="headline-4"
			mb={3}
			textColor="blue33"
			fontWeight="bold"
			textTransform="uppercase"
			fontSize={{ _: 1.75, l: 2 }}
			lineHeight={{ _: 2, l: 2.5 }}
		>
			{link.url ? <FooterLink link={link}>{link.name}</FooterLink> : link.name}
		</Text>
		{link.children && (
			<ul>
				{link.children.map((subLink, index) => (
					<Flex
						key={`footerSubLink${index}`}
						as="li"
						display="list-item"
						pb={3}
					>
						{subLink.url && (
							<FooterLink link={subLink}>{subLink.name}</FooterLink>
						)}
					</Flex>
				))}
			</ul>
		)}
	</FooterLinkSetWrapper>
)

const FooterLinksWrapper = styled.nav`
	width: 100%;
	column-count: 2;
	column-gap: ${scale.px(3)};

	${breakpoint("m")`
		column-count: 3;
    `}
	${breakpoint("l")`
		column-count: 4;
    `}
`

export interface FooterProps {
	children?: React.ReactNode
}

const Footer: React.FC<FooterProps> = () => {
	const { isBrowser } = useSSR()
	const { isLoading, data } = useQuery<FooterQueryProps>(
		"footer",
		() => getContent(`${PUBLIC_API_ENDPOINT}/settings/footer/`),
		{
			staleTime: queryStaleTime,
		}
	)
	const [showBackToTop, setshowBackToTop] = React.useState(false)

	/* Listeners for back to top button && scroll direction */
	let prevScrollY = window.scrollY

	const handleScroll = () => {
		const currentScrollY = window.scrollY

		const isUserAtBottom =
			window.innerHeight + window.scrollY >= document.body.clientHeight

		const isUserAtTop = currentScrollY === 0

		if (currentScrollY < prevScrollY || isUserAtBottom) {
			setshowBackToTop(true)
		} else {
			setshowBackToTop(false)
		}

		if (isUserAtTop) setshowBackToTop(false)

		prevScrollY = currentScrollY
	}

	const debouncedScrollHandler = React.useCallback(debounce(handleScroll, 10), [
		handleScroll,
	])
	React.useEffect(() => {
		if (isBrowser) {
			window.addEventListener("scroll", debouncedScrollHandler, { passive: true })

			return () => {
				window.removeEventListener("scroll", debouncedScrollHandler)
			}
		}
	}, [isBrowser])

	return (
		<Flex
			as="footer"
			w={1}
			backgroundColor="navy"
			textColor="white"
			flexDirection="column"
			pt={{ _: 7, l: 10 }}
			pb={21}
			px={{ _: 2, l: 6.5 }}
			mb={-2}
		>
			<Flex
				alignItems="center"
				justifyContent="flex-start"
				flexDirection="column"
				w={1}
				maxw={INNER_MAX_WIDTH}
				m="0 auto"
			>
				{!isLoading && data && (
					<>
						<Flex
							alignItems="flex-start"
							justifyContent={{ _: "flex-start", l: "space-between" }}
							w="100%"
							mb={5}
						>
							<Flex
								mt="0.5ex"
								mb={2}
								maxw={{ _: 18, l: `calc(25% - ${scale.px(2)})` }}
							>
								<Text.Link to={data?.logotypeLink ?? "#"}>
									<img
										src={assetBaseUrl() + data.logotype}
										alt={data.logotypeAltText ?? "Company logotype"}
										loading="lazy"
									/>
									<span className="visually-hidden">capio logo</span>
								</Text.Link>
							</Flex>
							<Flex
								width={`calc(${data.regionLogo ? "50%" : "75%"} - ${scale.px(
									1
								)})`}
								display={{ _: "none", l: "block" }}
							>
								<Text textColor="white" fontSize={2}>
									{data.description}
								</Text>
							</Flex>
							{data.regionLogo && (
								<Flex
									width={`calc(25% - ${scale.px(1)})`}
									display={{ _: "none", l: "block" }}
								>
									<img
										src={assetBaseUrl() + data.regionLogo}
										alt=""
										loading="lazy"
									/>
									<span className="visually-hidden">capio region logo</span>
								</Flex>
							)}
						</Flex>
						<FooterLinksWrapper>
							{data.menuItems.map((footerLinks, index) => (
								<FooterLinkSet link={footerLinks} key={`footerLink${index}`} />
							))}
						</FooterLinksWrapper>
						{data.regionLogo && (
							<Flex
								maxw={{ _: 25, l: `calc(100% - ${scale.px(1)})` }}
								display={{ _: "block", l: "none" }}
								alignSelf="baseline"
							>
								<img
									src={assetBaseUrl() + data.regionLogo}
									alt=""
									loading="lazy"
								/>
								<span className="visually-hidden">capio region logo</span>
							</Flex>
						)}
						<Flex
							w="auto"
							display={{ _: "block", l: "none" }}
							alignSelf="baseline"
							style={{
								marginTop: "40px",
							}}
						>
							<Text textColor="white" fontSize={1.75}>
								{data.description}
							</Text>
						</Flex>
					</>
				)}
			</Flex>
			{showBackToTop && <BackToTop />}
		</Flex>
	)
}

export { Footer }
